import { getFlattenedNodes } from "@ls/grouping-service";
import { isDefined } from "@ls/js-utils";
import { createSelector } from "reselect";
import { selectGetGroupingLevelOption } from "state/data/selectors";
import { selectRowMap as selectAnalyticRowMap } from "state/analytics/selectors";
import { selectRowMap as selectBookAnalyticRowMap } from "state/bookAnalytics/selectors";
import { selectRowSbsMap as selectAnalyticSbsRowMap } from "state/analytics/selectors";
import { selectRowMap as selectBetaRowMap } from "state/betas/selectors";
import { selectBetaSbsRowMap } from "state/betas/selectors";
import { selectRowMap as selectScenarioAnalysisRowMap } from "state/scenarioAnalysis/selectors";
import { selectRowSbsMap as selectScenarioAnalysisSbsRowMap } from "state/scenarioAnalysis/selectors";
import { selectRowMap as selectSecAttRowMap } from "state/securityAttributes/selectors";
import { selectRowMap as selectTERowMap } from "state/trackingError/selectors";
import { selectRowSbsMap as selectTESbsRowMap } from "state/trackingError/selectors";
import { selectRowMap as selectFaaRowMap } from "state/factorAttribution/selectors";
import { selectRowMap as selectIssueCountRowMap } from "state/issueCount/selectors";
import { selectRowMap as selectIssuerCountRowMap } from "state/issuerCount/selectors";
import { selectRowMap as selectTraceAnalyticsRowMap } from "state/traceAnalytics/selectors";
import { selectRowMap as selectRatingsRowMap } from "state/ratings/selectors";
import { selectSbsRowMap as selectRatingSbsRowMap } from "state/ratings/selectors";

import { selectRowMap as selectScan2RowMap } from "state/scan2/selectors";
import { selectVarSbsRowMap as selectScan2SbsRowMap } from "state/scan2/selectors";

export const selectGridInstance = (state) => state.grid.gridInstance;
export const selectGridStateId = (state) => state.grid.gridStateName;
export const selectBaseData = (state) => state.grid.baseData;
export const selectFetchingBaseData = (state) => state.grid.fetchingBaseData;

export const selectBaseSbsData = (state) => state.grid.baseSbsData;
export const selectFetchingBaseSbsData = (state) =>
  state.grid.fetchingBaseSbsData;

export const selectBaseRows = createSelector(
  selectBaseData,
  selectGetGroupingLevelOption,
  (baseData, getGroupingLevel) => {
    const rowsByPathString = {};

    const securityAttributeMap = baseData?.securityAttributes?.reduce(
      (map, secAttribute) => {
        map.set(secAttribute.primaryKey.securityId, secAttribute);
        return map;
      },
      new Map()
    );

    (getFlattenedNodes(baseData?.root) ?? []).forEach((node) => {
      if (!rowsByPathString[node.pathString]) {
        const row = {
          ...node,
          childNodesCount: node?.groupingNodes?.length ?? 0,
          securityCount: new Set(
            node?.entityReferences?.map(({ securityId }) => securityId) ?? []
          ).size,
          levelLabel:
            node.bucket !== "ROOT" ? getGroupingLevel(node.level)?.label : null,
        };
        if (isDefined(node?.securityId)) {
          row.securityRow = true;
          row.securityId = node.securityId;
          row.securityAttributes = securityAttributeMap.get(node.securityId);
        }
        rowsByPathString[node.pathString] = row;
      }
    });
    return Object.values(rowsByPathString);
  }
);

export const selectBaseSbsRows = createSelector(
  selectBaseSbsData,
  selectGetGroupingLevelOption,
  (baseSbsData, getGroupingLevel) => {
    const rowsByPathString = {};

    const securityAttributeMap = baseSbsData?.securityAttributes?.reduce(
      (map, secAttribute) => {
        map.set(secAttribute.primaryKey.securityId, secAttribute);
        return map;
      },
      new Map()
    );

    (getFlattenedNodes(baseSbsData?.root) ?? []).forEach((node) => {
      if (!rowsByPathString[node.pathString]) {
        const row = {
          ...node,
          childNodesCount: node?.groupingNodes?.length ?? 0,
          securityCount: new Set(
            node?.entityReferences?.map(({ securityId }) => securityId) ?? []
          ).size,
          levelLabel:
            node.bucket !== "ROOT" ? getGroupingLevel(node.level)?.label : null,
        };
        if (isDefined(node?.securityId)) {
          row.securityRow = true;
          row.securityId = node.securityId;
          row.securityAttributes = securityAttributeMap.get(node.securityId);
        }
        rowsByPathString[node.pathString] = row;
      }
    });
    return Object.values(rowsByPathString);
  }
);

export const selectRows = createSelector(
  selectBaseRows,
  selectBaseSbsRows,
  selectAnalyticRowMap,
  selectBookAnalyticRowMap,
  selectAnalyticSbsRowMap,
  selectBetaRowMap,
  selectBetaSbsRowMap,
  selectScenarioAnalysisRowMap,
  selectScenarioAnalysisSbsRowMap,
  selectSecAttRowMap,
  selectTERowMap,
  selectTESbsRowMap,
  selectTraceAnalyticsRowMap,
  selectFaaRowMap,
  selectIssueCountRowMap,
  selectIssuerCountRowMap,
  selectRatingsRowMap,
  selectRatingSbsRowMap,
  selectScan2RowMap,
  selectScan2SbsRowMap,
  (
    baseRows,
    baseSbsRows,
    analyticRowMap,
    bookAnalyticRowMap,
    analyticSbsRowMap,
    betaRowMap,
    betaSbsRowMap,
    scenarioAnalysisRowMap,
    scenarioAnalysisSbsRowMap,
    secAttRowMap,
    tERowMap,
    teSbsRowMap,
    taRowMap,
    faaRowMap,
    issueCountRowMap,
    issuerCountRowMap,
    ratingsRowMap,
    ratingsSbsRowMap,
    scan2RowMap,
    selectScan2SbsRowMap
  ) => {
    return [...baseRows, ...baseSbsRows].map((baseRow) => ({
      ...baseRow,
      ...(analyticRowMap[baseRow.pathString] ?? {}),
      ...(analyticSbsRowMap ? analyticSbsRowMap[baseRow?.pathString] : {}),
      ...(bookAnalyticRowMap[baseRow.pathString] ?? {}),
      ...(betaRowMap[baseRow.pathString] ?? {}),
      ...(betaSbsRowMap[baseRow.pathString] ?? {}),
      ...(scenarioAnalysisRowMap[baseRow.pathString] ?? {}),
      ...(scenarioAnalysisSbsRowMap[baseRow.pathString] ?? {}),
      ...(faaRowMap[baseRow.pathString] ?? {}),
      ...(issueCountRowMap[baseRow.pathString] ?? {}),
      ...(issuerCountRowMap[baseRow.pathString] ?? {}),
      ...(ratingsRowMap[baseRow.pathString] ?? {}),
      ...(ratingsSbsRowMap[baseRow.pathString] ?? {}),
      ...(secAttRowMap[baseRow.pathString] ?? {}),
      ...(tERowMap[baseRow.pathString] ?? {}),
      ...(teSbsRowMap[baseRow.pathString] ?? {}),
      ...(taRowMap[baseRow.pathString] ?? {}),
      ...(scan2RowMap[baseRow.pathString] ?? {}),
      ...(selectScan2SbsRowMap[baseRow.pathString] ?? {}),
    }));
  }
);
